import { useState } from 'react';
import { Home, HelpCircle, Download } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, PieChart, Pie, Cell } from 'recharts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from '@/components/ui/chart';
import ToolPage from '@/components/ToolPage';
import { toolCardClass } from '@/lib/toolStyles';

interface MortgageType {
  id: string;
  name: string;
  description: string;
  minTerm: number;
  maxTerm: number;
  typicalRate: number;
}

const mortgageTypes: MortgageType[] = [
  {
    id: 'fixed2',
    name: '2 Year Fixed',
    description: 'Fixed rate for 2 years, then variable',
    minTerm: 5,
    maxTerm: 40,
    typicalRate: 4.5
  },
  {
    id: 'fixed5',
    name: '5 Year Fixed',
    description: 'Fixed rate for 5 years, then variable',
    minTerm: 5,
    maxTerm: 40,
    typicalRate: 4.2
  },
  {
    id: 'tracker',
    name: 'Tracker',
    description: 'Rate tracks above Bank of England base rate',
    minTerm: 2,
    maxTerm: 40,
    typicalRate: 4.8
  }
];

const COLORS = ['#3b82f6', '#ef4444', '#10b981'];

export default function MortgageCalculator() {
  const [propertyValue, setPropertyValue] = useState<string>('250000');
  const [deposit, setDeposit] = useState<string>('50000');
  const [mortgageType, setMortgageType] = useState<string>('fixed5');
  const [interestRate, setInterestRate] = useState<string>('4.2');
  const [term, setTerm] = useState(25);
  const [showInfo, setShowInfo] = useState(false);
  const [overpayment, setOverpayment] = useState<string>('0');

  const selectedMortgageType = mortgageTypes.find(type => type.id === mortgageType)!;

  const calculateLTV = () => {
    const ltv = ((Number(propertyValue) - Number(deposit)) / Number(propertyValue)) * 100;
    return Math.round(ltv * 10) / 10;
  };

  const calculateMonthlyPayment = () => {
    const principal = Number(propertyValue) - Number(deposit);
    const monthlyRate = (Number(interestRate) / 100) / 12;
    const numberOfPayments = term * 12;

    if (monthlyRate === 0) return principal / numberOfPayments;

    const payment = principal * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

    return payment;
  };

  const calculateAmortizationSchedule = () => {
    const schedule = [];
    const principal = Number(propertyValue) - Number(deposit);
    const monthlyRate = (Number(interestRate) / 100) / 12;
    const monthlyPayment = calculateMonthlyPayment();
    const monthlyOverpayment = Number(overpayment);
    
    let balance = principal;
    let totalInterest = 0;
    let year = 1;
    let yearlyPrincipal = 0;
    let yearlyInterest = 0;

    for (let month = 1; month <= term * 12; month++) {
      const interestPayment = balance * monthlyRate;
      let principalPayment = monthlyPayment - interestPayment + monthlyOverpayment;
      
      if (principalPayment > balance) {
        principalPayment = balance;
      }

      balance = Math.max(0, balance - principalPayment);
      totalInterest += interestPayment;
      yearlyPrincipal += principalPayment;
      yearlyInterest += interestPayment;

      if (month % 12 === 0 || balance === 0) {
        schedule.push({
          year,
          balance,
          yearlyPrincipal,
          yearlyInterest,
          totalInterest
        });
        year++;
        yearlyPrincipal = 0;
        yearlyInterest = 0;
      }

      if (balance === 0) break;
    }

    return schedule;
  };

  const calculateTotalCost = () => {
    const schedule = calculateAmortizationSchedule();
    const lastEntry = schedule[schedule.length - 1];
    return (Number(propertyValue) - Number(deposit)) + lastEntry.totalInterest;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const downloadCalculations = () => {
    const schedule = calculateAmortizationSchedule();
    let csv = 'Mortgage Calculation Summary\n\n';
    
    csv += `Property Value,${propertyValue}\n`;
    csv += `Deposit,${deposit}\n`;
    csv += `Mortgage Type,${selectedMortgageType.name}\n`;
    csv += `Interest Rate,${interestRate}%\n`;
    csv += `Term,${term} years\n`;
    csv += `Monthly Payment,${calculateMonthlyPayment()}\n\n`;

    csv += 'Year,Balance,Principal Paid,Interest Paid,Total Interest\n';
    schedule.forEach(row => {
      csv += `${row.year},${row.balance},${row.yearlyPrincipal},${row.yearlyInterest},${row.totalInterest}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mortgage-calculation.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const schedule = calculateAmortizationSchedule();
  const monthlyPayment = calculateMonthlyPayment();
  const totalCost = calculateTotalCost();
  const ltv = calculateLTV();

  // Calculate breakdown for pie chart
  const totalInterest = schedule[schedule.length - 1].totalInterest;
  const breakdownData = [
    { name: 'Mortgage Amount', value: Number(propertyValue) - Number(deposit) },
    { name: 'Total Interest', value: totalInterest },
    { name: 'Deposit', value: Number(deposit) }
  ];

  const pieChartConfig = Object.fromEntries(breakdownData.map((d, i) => [d.name, { label: d.name, color: COLORS[i % COLORS.length] }]));
  const lineChartConfig = { balance: { label: 'Remaining Balance', color: 'hsl(var(--chart-5))' }, totalInterest: { label: 'Total Interest', color: 'hsl(var(--chart-1))' } };

  return (
    <ToolPage title="Mortgage Calculator" description="Calculate mortgage payments and total costs" icon={Home}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card className={toolCardClass}>
              <CardHeader className="flex flex-row justify-between items-center">
                <h2 className="text-lg font-semibold">Mortgage Details</h2>
                <Button variant="ghost" size="icon" onClick={() => setShowInfo(!showInfo)}><HelpCircle className="w-5 h-5" /></Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Property Value</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                    <Input type="number" min={0} value={propertyValue} onChange={(e) => setPropertyValue(e.target.value)} className="pl-8" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Deposit</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                    <Input type="number" min={0} value={deposit} onChange={(e) => setDeposit(e.target.value)} className="pl-8" />
                  </div>
                  <p className="text-sm text-muted-foreground">LTV: {ltv}%</p>
                </div>
                <div>
                  <Label>Mortgage Type</Label>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    {mortgageTypes.map((type) => (
                      <Button key={type.id} variant={mortgageType === type.id ? 'default' : 'outline'} size="sm" className="h-auto py-3" onClick={() => { setMortgageType(type.id); setInterestRate(type.typicalRate.toString()); }}>
                        {type.name}
                      </Button>
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{selectedMortgageType.description}</p>
                </div>
                <div className="space-y-2">
                  <Label>Interest Rate</Label>
                  <div className="relative">
                    <Input type="number" step={0.1} min={0} max={15} value={interestRate} onChange={(e) => setInterestRate(e.target.value)} className="pr-8" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">%</span>
                  </div>
                </div>
                <div>
                  <Label>Mortgage Term</Label>
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {[10, 15, 20, 25, 30].map((years) => (
                      <Button key={years} variant={term === years ? 'default' : 'outline'} size="sm" className="h-auto py-3" onClick={() => setTerm(years)}>{years}</Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Monthly Overpayment (Optional)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                    <Input type="number" min={0} value={overpayment} onChange={(e) => setOverpayment(e.target.value)} className="pl-8" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={toolCardClass}>
              <CardHeader className="flex flex-row justify-between items-center">
                <h2 className="text-lg font-semibold text-slate-900">Summary</h2>
                <Button variant="secondary" size="sm" onClick={downloadCalculations}>
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Monthly Payment</p>
                    <p className="text-2xl font-bold">{formatCurrency(monthlyPayment)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Total Cost</p>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(totalCost)}</p>
                  </div>
                </div>
                <ChartContainer config={pieChartConfig} className="mt-6 h-[300px] w-full">
                  <PieChart>
                    <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value))} />} />
                    <Pie data={breakdownData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>
                      {breakdownData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                    </Pie>
                    <ChartLegend content={<ChartLegendContent />} />
                  </PieChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            <Card className={toolCardClass}>
              <CardHeader>
                <h2 className="text-lg font-semibold">Repayment Schedule</h2>
              </CardHeader>
              <CardContent>
                <ChartContainer config={lineChartConfig} className="h-[400px] w-full mb-6">
                  <LineChart data={schedule}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="year" tickLine={false} axisLine={false} />
                    <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`} tickLine={false} axisLine={false} />
                    <ChartTooltip content={<ChartTooltipContent formatter={(value) => formatCurrency(Number(value))} labelFormatter={(label) => `Year ${label}`} />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Line type="monotone" dataKey="balance" name="Remaining Balance" stroke="var(--color-balance)" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="totalInterest" name="Total Interest" stroke="var(--color-totalInterest)" strokeWidth={2} dot={false} />
                  </LineChart>
                </ChartContainer>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Year</TableHead>
                      <TableHead>Balance</TableHead>
                      <TableHead>Principal</TableHead>
                      <TableHead>Interest</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schedule.map((row) => (
                      <TableRow key={row.year}>
                        <TableCell>{row.year}</TableCell>
                        <TableCell>{formatCurrency(row.balance)}</TableCell>
                        <TableCell>{formatCurrency(row.yearlyPrincipal)}</TableCell>
                        <TableCell>{formatCurrency(row.yearlyInterest)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card className="border-primary/20 bg-primary/5">
              <CardContent className="pt-6">
                <h3 className="font-semibold mb-2">Tips</h3>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li>Consider making overpayments to reduce the total interest paid</li>
                  <li>Check if your chosen mortgage allows overpayments without penalties</li>
                  <li>Remember to factor in other costs like insurance and maintenance</li>
                  <li>Review your mortgage regularly to ensure you are on the best rate</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
    </ToolPage>
  );
}